'use client'

import { useAuthorization } from '@entities/authentication'
import { useUserEntity } from '@entities/users'
import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { useI18n } from '@shared/config/i18n'
import { Modal } from '@shared/ui/modal'
import { useQuery } from '@tanstack/react-query'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

const CollectionSchema = z.object({
  status: z.enum(['NONE', 'GRACE', 'SUSPENDED', 'PENDING', 'UNSETTLED']),
  claimId: z.string().nullable(),
  renewalAmount: z.number().nullable(),
  regularAmount: z.number().nullable(),
  specialAmount: z.number().nullable(),
  differenceAmount: z.number().nullable(),
  nextChargeAt: z.string().nullable(),
  graceExpiresAt: z.string().nullable(),
  shouldDisplay: z.boolean(),
  displayKey: z.string().nullable(),
})

export function isCollectionExceptionPath(path: string) {
  return ['/my-podo/payment-methods', '/my-podo/customer-center', '/subscribes/jp', '/jp/payment', '/jp/checkout'].some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

const JST_OFFSET_MS = 9 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

export function japanDate(now = Date.now()) {
  return new Date(now + JST_OFFSET_MS).toISOString().slice(0, 10)
}

export function JpCollectionPopup() {
  const { bearerToken } = useAuthorization()
  const user = useUserEntity()
  const pathname = usePathname()
  const router = useRouter()
  const { t, effectiveLocale: locale } = useI18n()
  const [displayDate, setDisplayDate] = useState(() => japanDate())
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const refreshDate = () => {
      setDisplayDate(japanDate())
      clearTimeout(timer)
      const nextMidnight = Date.parse(`${japanDate()}T00:00:00Z`) + DAY_MS - JST_OFFSET_MS
      timer = setTimeout(refreshDate, Math.max(1, nextMidnight - Date.now()))
    }
    refreshDate()
    window.addEventListener('focus', refreshDate)
    document.addEventListener('visibilitychange', refreshDate)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('focus', refreshDate)
      document.removeEventListener('visibilitychange', refreshDate)
    }
  }, [])
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)
  const [openedKey, setOpenedKey] = useState<string | null>(null)
  const recorded = useRef(new Set<string>())
  const pending = useRef(new Set<string>())
  const inFlight = useRef(new Set<string>())
  const attempts = useRef(new Map<string, number>())
  const retryTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const currentAckKey = useRef('')
  useEffect(
    () => () => {
      currentAckKey.current = ''
      retryTimers.current.forEach(clearTimeout)
      retryTimers.current.clear()
    },
    [],
  )
  const enabled = Boolean(bearerToken) && user?.countryCode === 'JP' && !isCollectionExceptionPath(pathname)
  const { data, dataUpdatedAt } = useQuery({
    queryKey: ['jp-collection', user?.id, pathname, displayDate],
    enabled,
    queryFn: async () => {
      const response = await podoFetcher<{ data: unknown }>({
        url: '/api/v1/jp/collections/status',
        method: 'GET',
        header: { Authorization: `Bearer ${bearerToken}` },
      })
      return CollectionSchema.parse(response.data)
    },
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })
  const forced = data?.status === 'GRACE' || data?.status === 'SUSPENDED'
  const key = `${user?.id}:${displayDate}:${data?.displayKey ?? `${data?.status}:${data?.claimId}`}`
  const visible =
    enabled &&
    data !== undefined &&
    data.status !== 'NONE' &&
    (forced || ((data.shouldDisplay || openedKey === key) && key !== dismissedKey))
  useEffect(() => {
    if (!enabled || data?.status === 'NONE') {
      setOpenedKey(null)
    }
  }, [enabled, data?.status])
  currentAckKey.current = bearerToken && user?.countryCode === 'JP' ? key : ''
  useEffect(() => {
    if (visible && !forced) {
      setOpenedKey(key)
      pending.current.add(key)
    }
    if (!pending.current.has(key) || !data?.displayKey || forced || recorded.current.has(key)) {
      return
    }
    const displayKey = data.displayKey
    const send = () => {
      if (
        currentAckKey.current !== key ||
        japanDate() !== displayDate ||
        recorded.current.has(key) ||
        inFlight.current.has(key)
      ) {
        return
      }
      const timer = retryTimers.current.get(key)
      if (timer) {
        clearTimeout(timer)
      }
      retryTimers.current.delete(key)
      inFlight.current.add(key)
      attempts.current.set(key, (attempts.current.get(key) ?? 0) + 1)
      void podoFetcher({
        url: '/api/v1/jp/collections/displays',
        method: 'POST',
        header: { Authorization: `Bearer ${bearerToken}` },
        body: { displayKey },
      })
        .then(() => {
          recorded.current.add(key)
          pending.current.delete(key)
        })
        .catch(() => {
          // Three short retries, then the next successful status refresh can recover a pending acknowledgement.
          if (currentAckKey.current === key && japanDate() === displayDate && (attempts.current.get(key) ?? 0) < 3) {
            retryTimers.current.set(key, setTimeout(send, 5_000))
          }
        })
        .finally(() => {
          inFlight.current.delete(key)
        })
    }
    send()
  }, [visible, data?.displayKey, dataUpdatedAt, forced, bearerToken, key, displayDate])
  if (!visible || !data || data.status === 'NONE') {
    return null
  }
  const money = (amount: number | null) =>
    amount === null ? '—' : new Intl.NumberFormat(locale, { style: 'currency', currency: 'JPY' }).format(amount)
  const date = (value: string | null) =>
    value === null
      ? '—'
      : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Tokyo' }).format(
          new Date(value.endsWith('Z') || /[+-]\d\d:\d\d$/.test(value) ? value : `${value}+09:00`),
        )
  return (
    <Modal
      isOpen
      title={forced ? undefined : t(`jpCollection.${data.status}.title`)}
      closeOnEscape={!forced}
      closeOnOverlayClick={!forced}
      onClose={() => {
        if (!forced) {
          setDismissedKey(key)
        }
      }}
      aria-labelledby={forced ? 'jp-collection-title' : 'modal-title'}
    >
      <div className="space-y-4 p-4" data-testid="jp-collection-popup">
        {forced ? (
          <h2 id="jp-collection-title" className="text-xl font-bold">
            {t(`jpCollection.${data.status}.title`)}
          </h2>
        ) : null}
        {data.status === 'GRACE' ? <p>{t('jpCollection.GRACE.status')}</p> : null}
        <p className="whitespace-pre-line text-sm leading-6">{t(`jpCollection.${data.status}.body`)}</p>
        {forced ? (
          <dl className="space-y-2 rounded-lg bg-gray-50 p-4">
            <dt>{t('jpCollection.renewal')}</dt>
            <dd>{money(data.renewalAmount)}</dd>
            {data.graceExpiresAt ? (
              <>
                <dt>{t('jpCollection.deadline')}</dt>
                <dd>{date(data.graceExpiresAt)}</dd>
              </>
            ) : null}
          </dl>
        ) : (
          <div className="rounded-lg bg-gray-50 p-4">
            <strong className="text-2xl">{money(data.differenceAmount)}</strong>
            <p>
              {t('jpCollection.calculation', {
                regular: money(data.regularAmount),
                special: money(data.specialAmount),
              })}
            </p>
            <p className="text-xs">{t('jpCollection.cap')}</p>
            {data.status === 'PENDING' ? <p>{t('jpCollection.nextDate', { date: date(data.nextChargeAt) })}</p> : null}
          </div>
        )}
        {forced ? (
          <button
            className="w-full rounded-xl bg-primary p-3 font-bold text-white"
            onClick={() => router.push('/my-podo/payment-methods')}
          >
            {t('jpCollection.card')}
          </button>
        ) : null}
        <button
          className="w-full rounded-xl border p-3"
          onClick={() => router.push('/my-podo/customer-center/inquiries/new')}
        >
          {t('jpCollection.inquiry')}
        </button>
      </div>
    </Modal>
  )
}
